import type {
  AdminContactInquiryDetail,
  AdminContactInquiryListItem,
  AdminInquiryAiPhotos,
  AdminInquiryGeneration,
  AdminInquiryReferencePhoto,
  AdminInquiryRejectedReference,
  AdminReferencePhotoQualityMetrics,
  AdminReferencePhotoQualityVerdict,
  ContactInquiryCategory,
  ContactInquiryCounts,
  ContactInquiryPriority,
  ContactInquiryStatus,
  PaymentChannel,
} from '@fiket/shared'
import { CONTACT_INQUIRY_STATUS_LABELS } from '@fiket/shared'

export type {
  AdminContactInquiryDetail,
  AdminContactInquiryListItem,
  AdminInquiryAiPhotos,
  AdminInquiryGeneration,
  AdminInquiryReferencePhoto,
  AdminInquiryRejectedReference,
  AdminReferencePhotoQualityMetrics,
  AdminReferencePhotoQualityVerdict,
  ContactInquiryCategory,
  ContactInquiryCounts,
  ContactInquiryPriority,
  ContactInquiryStatus,
  PaymentChannel,
}
export {
  CONTACT_INQUIRY_CATEGORY_LABELS,
  CONTACT_INQUIRY_CLOSED_BY_LABELS,
  CONTACT_INQUIRY_PRIORITY_LABELS,
  CONTACT_INQUIRY_STATUS_LABELS,
} from '@fiket/shared'

export interface InquiriesResponse {
  items: AdminContactInquiryListItem[]
  counts: ContactInquiryCounts
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export type SortKey = 'lastMessageAt' | 'createdAt' | 'priority'
const SORT_KEYS: SortKey[] = ['lastMessageAt', 'createdAt', 'priority']
export function toSortKey(v: string | null): SortKey {
  return SORT_KEYS.includes(v as SortKey) ? (v as SortKey) : 'lastMessageAt'
}

/** 통계 카드 필터 키 — needsReply 는 OPEN + CUSTOMER_REPLIED 합, starred 는 별표(status 무관) */
export type StatsFilter = 'needsReply' | 'starred' | ContactInquiryStatus

/** URL 파라미터 → 활성 카드. needsReply > starred > status 우선. */
export function resolveStatsFilter(p: {
  needsReply: boolean
  starred: boolean
  status: ContactInquiryStatus | null
}): StatsFilter | null {
  if (p.needsReply) return 'needsReply'
  if (p.starred) return 'starred'
  return p.status
}

/** 답변 컴포저 상단 안내 — 등록 후 상태를 shared 라벨로 말한다. "완료" 는 CLOSED 에만 (FIKET-439). */
export function replyStatusHint(status: ContactInquiryStatus): string {
  const answered = CONTACT_INQUIRY_STATUS_LABELS.ANSWERED
  return status === 'CLOSED'
    ? `종결된 문의 — 답변을 등록하면 ${answered}(으)로 재오픈됩니다`
    : `등록하면 상태가 ${answered}(으)로 바뀝니다`
}

/** 카드 클릭 → URL. 카드끼리는 배타라 셋을 모두 지우고 하나만 세운다. 페이지는 1로. */
export function applyStatsFilter(next: URLSearchParams, key: StatsFilter | null): void {
  next.delete('needsReply')
  next.delete('starred')
  next.delete('status')
  next.delete('page')
  if (key === 'needsReply') next.set('needsReply', '1')
  else if (key === 'starred') next.set('starred', '1')
  else if (key) next.set('status', key)
}

export const STATUS_VALUES: ContactInquiryStatus[] = ['OPEN', 'CUSTOMER_REPLIED', 'ANSWERED', 'CLOSED']
export const PRIORITY_VALUES: ContactInquiryPriority[] = ['URGENT', 'HIGH', 'NORMAL', 'LOW']
export const CATEGORY_VALUES: ContactInquiryCategory[] = ['BUG', 'PAYMENT', 'ACCOUNT', 'FEATURE', 'OTHER']

export function isStatus(v: string | null): v is ContactInquiryStatus {
  return STATUS_VALUES.includes(v as ContactInquiryStatus)
}
export function isPriority(v: string | null): v is ContactInquiryPriority {
  return PRIORITY_VALUES.includes(v as ContactInquiryPriority)
}
export function isCategory(v: string | null): v is ContactInquiryCategory {
  return CATEGORY_VALUES.includes(v as ContactInquiryCategory)
}

// ── 접수 도메인 · 구매 채널 ─────────────────────────────────
// 접수 도메인(sourceHost)은 서버가 Origin/Referer 에서 뽑은 원시 hostname. 라벨링은 여기서만 한다.

/** prod 도메인 — 서버 필터(source=w / wedding)는 SMARTSTORE_WEB_URL / WEB_URL 의 hostname 과 정확 일치로 판정한다.
 *  dev(dev.fiket.ai·localhost)에서는 칩이 호스트 그대로 보이고 서버 필터는 dev 의 env 기준이라 둘이 어긋날 수 있다(의도). */
export const SMARTSTORE_HOST = 'w.fiket.ai'
export const WEDDING_HOST = 'wedding.fiket.ai'

/** 목록 필터 값 — URL `source` 파라미터. w: 스마트스토어 채널, wedding: 그 외 기록된 도메인 */
export type InquirySourceFilter = 'w' | 'wedding'
export const SOURCE_FILTER_VALUES: InquirySourceFilter[] = ['w', 'wedding']
export const SOURCE_FILTER_LABELS: Record<InquirySourceFilter, string> = {
  w: `스토어 (${SMARTSTORE_HOST})`,
  wedding: `홈페이지 (${WEDDING_HOST})`,
}
export function isSourceFilter(v: string | null): v is InquirySourceFilter {
  return SOURCE_FILTER_VALUES.includes(v as InquirySourceFilter)
}

/** 목록 필터 값 — URL `purchaseChannel` 파라미터. NONE = 정산 결제 없음 */
export type InquiryPurchaseFilter = PaymentChannel | 'NONE'
export const PURCHASE_FILTER_VALUES: InquiryPurchaseFilter[] = ['SMARTSTORE', 'SITE', 'NONE']
export const PURCHASE_FILTER_LABELS: Record<InquiryPurchaseFilter, string> = {
  SMARTSTORE: '스마트스토어 구매',
  SITE: '사이트(토스) 구매',
  NONE: '미결제',
}
export function isPurchaseFilter(v: string | null): v is InquiryPurchaseFilter {
  return PURCHASE_FILTER_VALUES.includes(v as InquiryPurchaseFilter)
}

/** 상세·CSV 와 같은 문구 (매출 관리 CHANNEL_LABELS 와 동일) */
export const PAYMENT_CHANNEL_LABELS: Record<PaymentChannel, string> = {
  SITE: '사이트(토스)',
  SMARTSTORE: '스마트스토어',
}
/** 목록 칩용 짧은 라벨 */
export const PAYMENT_CHANNEL_SHORT_LABELS: Record<PaymentChannel, string> = {
  SITE: '토스',
  SMARTSTORE: '스마트스토어',
}

export type InquirySourceKind = 'smartstore' | 'wedding' | 'other' | 'unknown'

/** 원시 hostname → 채널 종류. null(0099 이전 행) 은 unknown, dev/localhost 등은 other */
export function sourceKind(host: string | null | undefined): InquirySourceKind {
  if (!host) return 'unknown'
  if (host === SMARTSTORE_HOST) return 'smartstore'
  if (host === WEDDING_HOST) return 'wedding'
  return 'other'
}

/** 목록 칩·상세 라벨 — 알려진 도메인은 짧은 이름, 그 외는 호스트 그대로, 미기록은 '—' */
export function sourceLabel(host: string | null | undefined): string {
  switch (sourceKind(host)) {
    case 'smartstore':
      return '스토어(w)'
    case 'wedding':
      return '홈페이지'
    case 'other':
      return host as string
    default:
      return '—'
  }
}

/** 구매 채널 배열 → 상세·CSV 문구. 빈 배열(또는 구버전 응답의 undefined) = 미결제 */
export function purchaseChannelsText(channels: PaymentChannel[] | undefined): string {
  if (!channels || channels.length === 0) return '미결제'
  return channels.map((ch) => PAYMENT_CHANNEL_LABELS[ch]).join(' · ')
}

/** window 이벤트로 사이드바 배지·목록에 카운트 갱신을 알린다 */
export const INQUIRY_COUNTS_EVENT = 'fiket:inquiry-counts-changed'
export function notifyInquiryCountsChanged(): void {
  window.dispatchEvent(new Event(INQUIRY_COUNTS_EVENT))
}

// ── 결제·보상 카드 (FIKET-387) ─────────────────────────────

/** GET /api/admin/payments?userId= 응답 항목 (문의자 결제 목록) */
export interface InquiryPaymentRow {
  id: string
  type: string
  method: string
  amount: number
  creditsGranted: number | null
  status: string
  orderId: string | null
  paymentKey: string | null
  receiptUrl: string | null
  /** 누적 환불 금액·크레딧 (부분 환불 잔액 계산용) */
  refundedAmount: number
  refundedCredits: number
  createdAt: string
}

export interface InquiryPaymentsResponse {
  payments: InquiryPaymentRow[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
}

/** 실제 매출로 볼 상태 — FAILED/PENDING 시도는 기본 숨김 */
export const SETTLED_STATUSES = new Set(['COMPLETED', 'PARTIALLY_REFUNDED', 'REFUNDED'])

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  TOSS: '토스',
  NAVER_PAY: '스마트스토어',
  KAKAO_PAY: '카카오페이',
  CARD: '카드',
}

// 환불 잔액·가능 판정·요청/응답 타입은 매출 관리와 공용 (@/lib/tossRefund) — 기존 import 경로 유지용 재노출
export {
  refundRemaining,
  refundRemainingCredits,
  isTossRefundable,
  type TossAdminRefundRequest,
  type TossAdminRefundResponse,
} from '@/lib/tossRefund'
